import { For, type JSX, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import type { UtilBox } from '../lib/fleetUtilization';
import {
  canSetJobsAtOnce,
  createJobsAtOnceWrite,
  JOBS_AT_ONCE_CHOICES,
  jobsAtOnceMachine,
} from '../lib/jobs-at-once';
import { moveMenuFocus } from '../lib/menu-keys';
import { Field } from './Field';
import { Select } from './Select';

// A machine's "Jobs at once" cap (#6740, owner ruling V14; no prototype source, a v2
// addition listed in `docs/design/fleet-redesign/brief.md`): the most jobs it runs at the
// same time, 1 to 8. ONE presenter, two mounts:
//
//   - `row`: the details panel's row, closing About (`MachineAbout`). An admin or owner on this org's own machine gets the
//     shared `Select` with its hint; everyone else, and every machine from another org
//     (G21, G23), reads "Jobs at once: <n>" as plain text.
//   - `picker`: the card menu's body once its "Jobs at once" item is chosen
//     (`MissionCardMenu`): eight `menuitemradio` rows, the stored one checked. ↑ ↓ wrap,
//     Home End jump (`moveMenuFocus`), Enter or Space saves, Escape hands back to the main
//     menu (`onBack`) instead of closing it — and does nothing while a save is in flight,
//     so its outcome is seen — Tab closes it. The group carries the name. The menu only offers the item
//     to a viewer who may write (`cardMenu`).
//
// The value shown is always `box.max_slots`, the utilization read; the write state is
// `createJobsAtOnceWrite` (`lib/jobs-at-once.ts`), which re-reads after every save. While
// a save is in flight the control is disabled and says "Saving…"; a refusal shows the
// API's detail, and the shown value is the stored one it never left.

export type MachineJobsAtOnceProps = {
  readonly box: UtilBox;
  /** `canAdminWrite` on a settled role (owner ruling G21). */
  readonly canWrite: boolean;
  readonly variant: 'row' | 'picker';
  /** Defaults to the session's token, as `setBoxPrompt` does. */
  readonly token?: string | null | undefined;
  /** picker: the save landed, close the menu. */
  readonly onSaved?: () => void;
  /** picker: Escape, back to the main menu body. */
  readonly onBack?: () => void;
  /** picker: Tab, close the menu. */
  readonly onClose?: () => void;
};

const FIELD_ID = 'machine-jobs-at-once';

export function MachineJobsAtOnce(props: MachineJobsAtOnceProps): JSX.Element {
  const write = createJobsAtOnceWrite({
    box: () => props.box,
    token: props.token,
    onSaved: () => props.onSaved?.(),
  });
  const label = (): string => t('fleet.jobs_at_once.label');
  const status = (
    <>
      <Show when={write.pending()}>
        <p class="field__desc" role="status">
          {t('fleet.jobs_at_once.saving')}
        </p>
      </Show>
    </>
  );

  if (props.variant === 'picker') {
    let menu: HTMLDivElement | undefined;
    const pick = (n: number): void => {
      if (write.pending()) return;
      // A pick of the stored value changes nothing: the menu closes, as `Select` sends
      // nothing for an unchanged pick.
      if (n === props.box.max_slots) props.onSaved?.();
      else write.save(n);
    };
    onMount(() => menu?.querySelector<HTMLElement>('[aria-checked="true"]')?.focus());
    const name = (): string =>
      t('fleet.jobs_at_once.picker_label', { machine: jobsAtOnceMachine(props.box) });
    return (
      <>
        <div
          ref={menu}
          role="menu"
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              // Consumed either way, so `<Popover/>` does not close over a save in flight;
              // the person stays on the picker until it settles and sees its outcome.
              e.preventDefault();
              if (!write.pending()) props.onBack?.();
            } else if (e.key === 'Tab') props.onClose?.();
            else if (menu && moveMenuFocus(menu, e.key)) e.preventDefault();
          }}
        >
          <fieldset class="machine-menu__picker" aria-label={name()}>
            <For each={JOBS_AT_ONCE_CHOICES}>
              {(n) => (
                <button
                  type="button"
                  role="menuitemradio"
                  class="popover__item"
                  aria-checked={n === props.box.max_slots ? 'true' : 'false'}
                  aria-disabled={write.pending() ? 'true' : undefined}
                  onClick={() => pick(n)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    // Handled here, not by the native click, so Enter and Space save the
                    // same way under every engine.
                    e.preventDefault();
                    pick(n);
                  }}
                >
                  {n}
                </button>
              )}
            </For>
          </fieldset>
        </div>
        {status}
        <Show when={write.error()}>
          {(msg) => (
            <p class="field__error" role="alert">
              {msg()}
            </p>
          )}
        </Show>
      </>
    );
  }

  // Inside About's section, whose flex gap spaces it from the facts above.
  return (
    <div>
      <Show
        when={canSetJobsAtOnce(props.box, props.canWrite)}
        fallback={
          <p class="facts__num">
            {t('fleet.jobs_at_once.read_only', { count: props.box.max_slots })}
          </p>
        }
      >
        <Field
          label={label()}
          id={FIELD_ID}
          desc={t('fleet.jobs_at_once.hint')}
          error={write.error()}
        >
          {(a11y) => (
            <Select
              a11y={a11y}
              label={label()}
              value={String(props.box.max_slots)}
              options={JOBS_AT_ONCE_CHOICES.map((n) => ({ value: String(n), label: String(n) }))}
              disabled={write.pending()}
              onChange={(value) => write.save(Number(value))}
            />
          )}
        </Field>
        {status}
      </Show>
    </div>
  );
}
